import React, { useState } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';

export function BenHillGriffinInteractiveMap({ onLocationSelect, onClose }) {
  const [hoveredZone, setHoveredZone] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [showLabels, setShowLabels] = useState(true);
  const [visibleLayers, setVisibleLayers] = useState({
    accessible: true,
    quietRoom: true,
    nursingRoom: true,
    firstAid: true,
    restrooms: true,
    concessions: true,
    loadZone: true
  });

  const handleZoneClick = (zoneId, zoneName, zoneType) => {
    setSelectedZone({ id: zoneId, name: zoneName, type: zoneType });
    onLocationSelect?.(zoneId);
  };

  const toggleLayer = (layer) => {
    setVisibleLayers(prev => ({ ...prev, [layer]: !prev[layer] }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0021A5] to-[#FA4616] text-white p-3 md:p-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
              alt="Logo"
              className="w-16 h-16 md:w-20 md:h-20 object-contain"
            />
            <div>
              <h2 className="text-lg md:text-xl font-bold" style={{ fontFamily: "Lexend, sans-serif" }}>
                Ben Hill Griffin Stadium
              </h2>
              <p className="text-sm text-white/90 font-medium">XTERMIGATOR Kids Inclusive Game Day</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close map"
            >
              <X size={24} />
            </button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
          {/* Legend Sidebar */}
          <div className="md:w-56 bg-[#f0f4f8] p-3 overflow-y-auto border-r order-2 md:order-1">
            {/* Legend Card */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-4">
              <div className="bg-[#0021A5] text-white px-3 py-2">
                <h3 className="font-bold text-sm">LEGEND</h3>
              </div>
              <div className="p-3 space-y-2">
                <LegendItem 
                  icon={<ElevatorIcon />} 
                  label="Elevators" 
                  active={true}
                />
                <LegendItem 
                  icon={<AccessibleIcon />} 
                  label="Accessible Seating" 
                  active={visibleLayers.accessible}
                  onClick={() => toggleLayer('accessible')}
                />
                <LegendItem 
                  icon={<RestroomIcon />} 
                  label="Restrooms" 
                  active={visibleLayers.restrooms}
                  onClick={() => toggleLayer('restrooms')}
                />
                <LegendItem 
                  icon={<ConcessionIcon />} 
                  label="Concessions" 
                  active={visibleLayers.concessions}
                  onClick={() => toggleLayer('concessions')}
                />
                <LegendItem 
                  icon={<QuestionIcon />} 
                  label="Questions" 
                  active={true}
                />
                <LegendItem 
                  icon={<QuietRoomIcon />} 
                  label="Quiet Room" 
                  active={visibleLayers.quietRoom}
                  onClick={() => toggleLayer('quietRoom')}
                  highlight
                />
                <LegendItem 
                  icon={<NursingIcon />} 
                  label="Nursing Room" 
                  active={visibleLayers.nursingRoom}
                  onClick={() => toggleLayer('nursingRoom')}
                />
                <LegendItem 
                  icon={<FirstAidIcon />} 
                  label="First Aid" 
                  active={visibleLayers.firstAid}
                  onClick={() => toggleLayer('firstAid')}
                />
                <LegendItem 
                  icon={<LoadZoneIcon />} 
                  label="Load Zone" 
                  active={visibleLayers.loadZone}
                  onClick={() => toggleLayer('loadZone')}
                />
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="bg-white rounded-xl shadow-md p-3 mb-4">
              <h3 className="font-bold text-xs text-gray-600 mb-2">VIEW CONTROLS</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setZoom(Math.min(zoom + 0.2, 2))}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-[#0021A5] hover:bg-[#001580] rounded text-xs font-medium text-white"
                >
                  <ZoomIn size={14} />
                </button>
                <button
                  onClick={() => setZoom(Math.max(zoom - 0.2, 0.6))}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-[#0021A5] hover:bg-[#001580] rounded text-xs font-medium text-white"
                >
                  <ZoomOut size={14} />
                </button>
                <button
                  onClick={() => setZoom(1)}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-2 bg-gray-200 hover:bg-gray-300 rounded text-xs font-medium text-gray-700"
                >
                  <Maximize2 size={14} />
                </button>
              </div>
            </div>

            {/* Website Link */}
            <a 
              href="https://www.inclusivegameday.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block bg-gradient-to-r from-[#0021A5] to-[#FA4616] rounded-xl p-3 text-white text-center hover:opacity-90 transition-opacity"
            >
              <p className="text-xs">More Resources</p>
              <p className="text-sm font-bold">inclusivegameday.com</p>
            </a>
          </div>

          {/* Main Map Area */}
          <div className="flex-1 overflow-auto bg-[#e8eef4] p-2 md:p-4 order-1 md:order-2">
            <div 
              className="mx-auto transition-transform duration-300"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center top' }}
            >
              <Stadium3DSVG
                hoveredZone={hoveredZone}
                selectedZone={selectedZone}
                onZoneHover={setHoveredZone}
                onZoneClick={handleZoneClick}
                visibleLayers={visibleLayers}
                showLabels={showLabels}
              />
            </div>

            {/* Hover Tooltip */}
            {hoveredZone && (
              <div className="fixed bottom-20 md:bottom-8 left-1/2 transform -translate-x-1/2 bg-[#0021A5] text-white px-4 py-2 rounded-lg shadow-xl z-50">
                <p className="font-semibold text-sm">{hoveredZone}</p>
                <p className="text-xs text-blue-200">Click for details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Legend Item Component
function LegendItem({ icon, label, active, onClick, highlight }) {
  return (
    <div 
      className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${
        active ? 'opacity-100' : 'opacity-40'
      } ${highlight ? 'bg-orange-100' : 'hover:bg-gray-100'}`}
      onClick={onClick}
    >
      <div className="w-6 h-6 flex items-center justify-center">{icon}</div>
      <span className="text-xs font-medium text-gray-700">{label}</span>
    </div>
  );
}

// Icon Components
function ElevatorIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="4" y="2" width="16" height="20" rx="2" fill="#0021A5"/>
      <circle cx="12" cy="8" r="2.5" fill="white"/>
      <path d="M8 13 L12 18 L16 13" fill="none" stroke="white" strokeWidth="2"/>
    </svg>
  );
}

function AccessibleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <circle cx="12" cy="12" r="11" fill="#0021A5"/>
      <circle cx="12" cy="7" r="2" fill="white"/>
      <path d="M12 10 L12 14 L9 14 M12 14 L12 18 L15 20 M12 14 L15 14" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function RestroomIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#0021A5"/>
      <circle cx="8" cy="6" r="2" fill="white"/>
      <rect x="6" y="9" width="4" height="8" rx="1" fill="white"/>
      <circle cx="16" cy="6" r="2" fill="white"/>
      <path d="M14 9 L14 13 L13 17 M18 9 L18 13 L19 17" stroke="white" strokeWidth="1.5" fill="none"/>
      <rect x="14" y="9" width="4" height="5" fill="white"/>
    </svg>
  );
}

function ConcessionIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#FA4616"/>
      <path d="M7 8 L7 18 L10 18 L10 14 L14 14 L14 18 L17 18 L17 8" fill="white"/>
      <rect x="9" y="10" width="6" height="3" fill="#FA4616"/>
    </svg>
  );
}

function QuestionIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" fill="#0021A5"/>
      <text x="12" y="17" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">?</text>
    </svg>
  );
}

function QuietRoomIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#FA4616"/>
      <text x="12" y="17" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">Z</text>
    </svg>
  );
}

function NursingIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#0021A5"/>
      <circle cx="12" cy="8" r="3" fill="white"/>
      <ellipse cx="12" cy="16" rx="5" ry="4" fill="white"/>
    </svg>
  );
}

function FirstAidIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#dc2626"/>
      <rect x="10" y="6" width="4" height="12" fill="white"/>
      <rect x="6" y="10" width="12" height="4" fill="white"/>
    </svg>
  );
}

function LoadZoneIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5">
      <rect x="2" y="2" width="20" height="20" rx="3" fill="#FA4616"/>
      <rect x="5" y="10" width="14" height="8" rx="1" fill="white"/>
      <circle cx="8" cy="18" r="2" fill="#FA4616"/>
      <circle cx="16" cy="18" r="2" fill="#FA4616"/>
    </svg>
  );
}

// Main 3D Stadium SVG
function Stadium3DSVG({ 
  hoveredZone, 
  selectedZone, 
  onZoneHover, 
  onZoneClick, 
  visibleLayers,
  showLabels 
}) {
  const isSelected = (id) => selectedZone?.id === id;

  return (
    <svg 
      viewBox="0 0 1000 750" 
      className="w-full h-auto rounded-xl shadow-lg"
      xmlns="http://www.w3.org/2000/svg"
      style={{ background: 'linear-gradient(180deg, #87CEEB 0%, #e0e8f0 100%)' }}
    >
      {/* Sky gradient background */}
      <defs>
        <linearGradient id="skyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#87CEEB"/>
          <stop offset="100%" stopColor="#c8d8e8"/>
        </linearGradient>
        <linearGradient id="fieldGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#228B22"/>
          <stop offset="50%" stopColor="#1a7a1a"/>
          <stop offset="100%" stopColor="#228B22"/>
        </linearGradient>
      </defs>
      
      <rect width="1000" height="750" fill="url(#skyGrad)"/>

      {/* Stadium Base/Ground */}
      <ellipse cx="500" cy="650" rx="450" ry="80" fill="#8B7355" opacity="0.3"/>

      {/* ========== STADIUM BOWL - 3D ISOMETRIC ========== */}
      
      {/* Back/Far Side Seating (Blue sections) */}
      <path 
        d="M 150 200 Q 500 50 850 200 L 820 280 Q 500 150 180 280 Z" 
        fill="#0021A5"
        stroke="#001580"
        strokeWidth="2"
      />
      
      {/* Left Side Seating (Orange) */}
      <path 
        d="M 100 350 L 150 200 L 180 280 L 140 420 Z" 
        fill="#FA4616"
        stroke="#d93a0f"
        strokeWidth="2"
      />
      
      {/* Right Side Seating (Orange) */}
      <path 
        d="M 900 350 L 850 200 L 820 280 L 860 420 Z" 
        fill="#FA4616"
        stroke="#d93a0f"
        strokeWidth="2"
      />

      {/* Front/Near Side Upper (Blue) */}
      <path 
        d="M 180 500 Q 500 420 820 500 L 860 420 Q 500 320 140 420 Z" 
        fill="#0021A5"
        stroke="#001580"
        strokeWidth="2"
      />

      {/* Front/Near Side Lower (Blue) */}
      <path 
        d="M 200 580 Q 500 510 800 580 L 820 500 Q 500 420 180 500 Z" 
        fill="#3b5998"
        stroke="#0021A5"
        strokeWidth="2"
      />

      {/* ========== FOOTBALL FIELD ========== */}
      <g transform="translate(250, 280)">
        {/* Field base */}
        <rect x="0" y="0" width="500" height="220" fill="url(#fieldGrad)" rx="5"/>
        
        {/* End zones */}
        <rect x="0" y="0" width="50" height="220" fill="#0021A5" opacity="0.6"/>
        <rect x="450" y="0" width="50" height="220" fill="#FA4616" opacity="0.6"/>
        
        {/* Yard lines */}
        {[50, 100, 150, 200, 250, 300, 350, 400, 450].map((x, i) => (
          <line key={i} x1={x} y1="0" x2={x} y2="220" stroke="white" strokeWidth="1" opacity="0.5"/>
        ))}
        
        {/* Center line */}
        <line x1="250" y1="0" x2="250" y2="220" stroke="white" strokeWidth="2"/>
        
        {/* Gators Logo Center */}
        <ellipse cx="250" cy="110" rx="60" ry="40" fill="#0021A5" opacity="0.8"/>
        <text x="250" y="100" textAnchor="middle" fill="white" fontSize="20" fontWeight="bold">🐊</text>
        <text x="250" y="125" textAnchor="middle" fill="#FA4616" fontSize="14" fontWeight="bold">GATORS</text>
        
        {/* Yard numbers */}
        <text x="100" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">40</text>
        <text x="150" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">30</text>
        <text x="200" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">20</text>
        <text x="300" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">20</text>
        <text x="350" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">30</text>
        <text x="400" y="115" textAnchor="middle" fill="white" fontSize="16" opacity="0.7">40</text>
        
        {/* GATORS text on end zones */}
        <text x="25" y="115" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" transform="rotate(-90, 25, 115)">GATORS</text>
        <text x="475" y="115" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" transform="rotate(90, 475, 115)">SWAMP</text>
      </g>

      {/* ========== STUDENT SECTION with Warning ========== */}
      <g 
        className="cursor-pointer"
        onMouseEnter={() => onZoneHover('Student Section - HIGH NOISE WARNING')}
        onMouseLeave={() => onZoneHover(null)}
        onClick={() => onZoneClick('student', 'Student Section', 'warning')}
      >
        <path 
          d="M 750 180 L 850 200 L 820 280 L 730 260 Z" 
          fill="#FA4616"
          stroke="#d93a0f"
          strokeWidth="3"
          opacity={isSelected('student') ? 1 : 0.9}
        />
        {/* Warning label */}
        <rect x="745" y="195" width="110" height="45" rx="5" fill="white" stroke="#FA4616" strokeWidth="2"/>
        <text x="800" y="212" textAnchor="middle" fill="#0021A5" fontSize="11" fontWeight="bold">Student Section</text>
        <text x="800" y="232" textAnchor="middle" fill="#FA4616" fontSize="18">⚠️ ⚡</text>
      </g>

      {/* ========== BAND AREA ========== */}
      <g 
        className="cursor-pointer"
        onMouseEnter={() => onZoneHover('Band Area - Loud Music')}
        onMouseLeave={() => onZoneHover(null)}
      >
        <ellipse cx="200" cy="250" rx="40" ry="25" fill="#FA4616" stroke="white" strokeWidth="2"/>
        <rect x="155" y="215" width="90" height="25" rx="4" fill="white" stroke="#FA4616" strokeWidth="2"/>
        <text x="200" y="232" textAnchor="middle" fill="#0021A5" fontSize="11" fontWeight="bold">Band Area</text>
        <text x="200" y="255" textAnchor="middle" fontSize="16">🎺</text>
      </g>

      {/* ========== NURSING ROOM ========== */}
      {visibleLayers.nursingRoom && (
        <g 
          className="cursor-pointer"
          onMouseEnter={() => onZoneHover('Nursing Room')}
          onMouseLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('nursing', 'Nursing Room', 'nursing')}
        >
          <rect x="380" y="100" width="100" height="35" rx="5" fill="white" stroke="#0021A5" strokeWidth="2"/>
          <text x="430" y="122" textAnchor="middle" fill="#0021A5" fontSize="11" fontWeight="bold">Nursing Room</text>
          <circle cx="360" cy="118" r="15" fill="#0021A5"/>
          <circle cx="360" cy="114" r="4" fill="white"/>
          <ellipse cx="360" cy="123" rx="6" ry="5" fill="white"/>
        </g>
      )}

      {/* ========== ACCESSIBLE SEATING ICONS ========== */}
      {visibleLayers.accessible && (
        <>
          {/* Top right */}
          <AccessibleMarker x={780} y={140} onClick={() => onZoneClick('ada-1', 'Accessible Seating North', 'accessible')} onHover={onZoneHover} />
          
          {/* Top center */}
          <AccessibleMarker x={500} y={160} onClick={() => onZoneClick('ada-2', 'Accessible Seating Center', 'accessible')} onHover={onZoneHover} />
          
          {/* Bottom left */}
          <AccessibleMarker x={250} y={540} onClick={() => onZoneClick('ada-3', 'Accessible Seating Southwest', 'accessible')} onHover={onZoneHover} />
          
          {/* Bottom right */}
          <AccessibleMarker x={750} y={540} onClick={() => onZoneClick('ada-4', 'Accessible Seating Southeast', 'accessible')} onHover={onZoneHover} />
          
          {/* Right side */}
          <AccessibleMarker x={870} y={350} onClick={() => onZoneClick('ada-5', 'Accessible Seating East', 'accessible')} onHover={onZoneHover} />
        </>
      )}

      {/* ========== RESTROOMS ========== */}
      {visibleLayers.restrooms && (
        <>
          <RestroomMarker x={180} y={350} onClick={() => onZoneClick('restroom-1', 'Restrooms West', 'restroom')} onHover={onZoneHover} />
          <RestroomMarker x={600} y={550} onClick={() => onZoneClick('restroom-2', 'Restrooms South', 'restroom')} onHover={onZoneHover} />
        </>
      )}

      {/* ========== FIRST AID ========== */}
      {visibleLayers.firstAid && (
        <g 
          className="cursor-pointer"
          onMouseEnter={() => onZoneHover('First Aid Station')}
          onMouseLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('firstaid', 'First Aid', 'medical')}
        >
          <rect x="350" y="530" width="80" height="30" rx="5" fill="white" stroke="#dc2626" strokeWidth="2"/>
          <text x="390" y="550" textAnchor="middle" fill="#0021A5" fontSize="10" fontWeight="bold">First Aid</text>
          <rect x="320" y="535" width="25" height="20" rx="3" fill="#dc2626"/>
          <rect x="329" y="540" width="7" height="10" fill="white"/>
          <rect x="326" y="543" width="13" height="4" fill="white"/>
        </g>
      )}

      {/* ========== CONCESSIONS ========== */}
      {visibleLayers.concessions && (
        <g 
          className="cursor-pointer"
          onMouseEnter={() => onZoneHover('Concessions')}
          onMouseLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('concession', 'Concessions', 'concession')}
        >
          <rect x="680" y={440} width="90" height="30" rx="5" fill="white" stroke="#FA4616" strokeWidth="2"/>
          <text x="725" y="460" textAnchor="middle" fill="#0021A5" fontSize="10" fontWeight="bold">Concessions</text>
          <rect x="650" y="445" width="25" height="20" rx="3" fill="#FA4616"/>
          <text x="662" y="460" textAnchor="middle" fill="white" fontSize="12">🍿</text>
        </g>
      )}

      {/* ========== QUIET ROOM ========== */}
      {visibleLayers.quietRoom && (
        <g 
          className="cursor-pointer"
          onMouseEnter={() => onZoneHover('Quiet Room - Sensory Break Space')}
          onMouseLeave={() => onZoneHover(null)}
          onClick={() => onZoneClick('quiet', 'Quiet Room', 'quiet')}
        >
          <rect x="100" y="430" width="80" height="35" rx="5" fill="#FA4616"/>
          <text x="140" y="453" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Quiet Room</text>
          <rect x="70" y="438" width="25" height="20" rx="3" fill="#FA4616" stroke="white" strokeWidth="2"/>
          <text x="82" y="453" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">Z</text>
        </g>
      )}

      {/* ========== LOAD ZONES ========== */}
      {visibleLayers.loadZone && (
        <>
          {/* Left Load Zone */}
          <g 
            className="cursor-pointer"
            onMouseEnter={() => onZoneHover('Load Zone - Wheelchair Van Access')}
            onMouseLeave={() => onZoneHover(null)}
          >
            <rect x="50" y="580" width="80" height="30" rx="5" fill="#FA4616"/>
            <text x="90" y="600" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">LOAD ZONE</text>
            {/* Van icon */}
            <rect x="30" y="620" width="50" height="25" rx="3" fill="white" stroke="#333" strokeWidth="1"/>
            <circle cx="45" cy="645" r="6" fill="#333"/>
            <circle cx="65" cy="645" r="6" fill="#333"/>
            <rect x="55" y="622" width="20" height="15" fill="#87CEEB"/>
          </g>

          {/* Right Load Zone */}
          <g 
            className="cursor-pointer"
            onMouseEnter={() => onZoneHover('Load Zone - Wheelchair Van Access')}
            onMouseLeave={() => onZoneHover(null)}
          >
            <rect x="870" y="580" width="80" height="30" rx="5" fill="#FA4616"/>
            <text x="910" y="600" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">LOAD ZONE</text>
          </g>
        </>
      )}

      {/* ========== DISABILITIES ENTRANCE ========== */}
      <g 
        className="cursor-pointer"
        onMouseEnter={() => onZoneHover('Disabilities Entrance')}
        onMouseLeave={() => onZoneHover(null)}
        onClick={() => onZoneClick('entrance', 'Disabilities Entrance', 'entrance')}
      >
        {/* Entrance Building */}
        <rect x="420" y="620" width="160" height="60" rx="5" fill="#d4c4a8" stroke="#8B7355" strokeWidth="2"/>
        <rect x="480" y="650" width="40" height="30" fill="#5c4a32"/>
        <text x="500" y="640" textAnchor="middle" fill="#333" fontSize="11" fontWeight="bold">EN GRADDESS</text>
        
        {/* Accessibility Sign */}
        <g transform="translate(500, 700)">
          <rect x="-40" y="-15" width="80" height="25" rx="3" fill="#0021A5"/>
          <circle cx="-20" cy="-2" r="8" fill="white"/>
          <text x="-20" y="2" textAnchor="middle" fill="#0021A5" fontSize="10">♿</text>
          <text x="15" y="3" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">DISABILITIES</text>
          <text x="15" y="10" textAnchor="middle" fill="white" fontSize="6">ENTRANCE</text>
        </g>
      </g>

      {/* ========== TITLE ========== */}
      <rect x="300" y="10" width="400" height="35" rx="5" fill="white" opacity="0.9"/>
      <text x="500" y="33" textAnchor="middle" fill="#0021A5" fontSize="16" fontWeight="bold" style={{ fontFamily: "sans-serif" }}>
        Ben Hill Griffin Stadium Interactive Map
      </text>
    </svg>
  );
}

// Accessible Seating Marker
function AccessibleMarker({ x, y, onClick, onHover }) {
  return (
    <g 
      className="cursor-pointer"
      onMouseEnter={() => onHover('Accessible Seating')}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      <circle cx={x} cy={y} r="18" fill="white" stroke="#0021A5" strokeWidth="2"/>
      <circle cx={x} cy={y-4} r="4" fill="#0021A5"/>
      <path d={`M${x} ${y} L${x} ${y+6} L${x-5} ${y+6} M${x} ${y+6} L${x} ${y+12} L${x+5} ${y+14}`} stroke="#0021A5" strokeWidth="2" fill="none"/>
    </g>
  );
}

// Restroom Marker
function RestroomMarker({ x, y, onClick, onHover }) {
  return (
    <g 
      className="cursor-pointer"
      onMouseEnter={() => onHover('Restrooms')}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      <rect x={x-35} y={y-12} width="95" height="30" rx="5" fill="white" stroke="#0021A5" strokeWidth="2"/>
      <text x={x+15} y={y+5} textAnchor="middle" fill="#0021A5" fontSize="10" fontWeight="bold">Restrooms</text>
      <circle cx={x-20} cy={y} r="12" fill="#0021A5"/>
      <circle cx={x-20} cy={y-5} r="3" fill="white"/>
      <rect x={x-23} y={y-1} width="6" height="8" fill="white"/>
    </g>
  );
}
